import { memo, useEffect, useRef } from 'react';
import { useShop, useEffects } from '@/contexts';
import { PlayerStatusBar } from '@/components/ui';
import type { GameState } from '@/types/game';
import { getWorldForLevel } from '@/config/worlds';
import { COLOR } from '@/config/designTokens';

interface HUDProps {
  gameState?: GameState;
}

const HUDComponent = ({ gameState: propsGameState }: HUDProps) => {
  const { shopState, shopManager } = useShop();
  const { triggerEffect } = useEffects();
  
  // Use props gameState if provided
  const gameState = propsGameState || { isPlaying: false, gameStats: { score: 0 } } as GameState;
  
  const prevCoinsRef = useRef(shopState.coins);
  const prevGemsRef = useRef(shopState.gems);
  const prevScoreRef = useRef(gameState.gameStats.score);
  
  // Get streak info
  const streakStatus = shopManager?.getDailyRewardStatus() || { streak: 0, canClaim: false };
  const world = getWorldForLevel(gameState.level || 0);

  // Trigger coin collection effect when coins increase
  useEffect(() => {
    if (shopState.coins > prevCoinsRef.current) {
      const coinDiff = shopState.coins - prevCoinsRef.current;
      triggerEffect({
        type: 'coinCollect',
        position: [85, 10], // Near coins display
        intensity: coinDiff,
        duration: 1000
      });
    }
    prevCoinsRef.current = shopState.coins;
  }, [shopState.coins, triggerEffect]);

  // Trigger gem collection effect when gems increase
  useEffect(() => {
    if (shopState.gems > prevGemsRef.current) {
      const gemDiff = shopState.gems - prevGemsRef.current;
      triggerEffect({
        type: 'sparkle',
        position: [85, 20], // Near gems display
        intensity: gemDiff,
        duration: 1000,
        color: COLOR.gem
      });
    }
    prevGemsRef.current = shopState.gems;
  }, [shopState.gems, triggerEffect]);

  // Trigger sparkle effect when score increases
  useEffect(() => {
    if (gameState.gameStats.score > prevScoreRef.current) {
      triggerEffect({
        type: 'sparkle',
        position: [15, 10], // Near score display
        duration: 800
      });
    }
    prevScoreRef.current = gameState.gameStats.score;
  }, [gameState.gameStats.score, triggerEffect]);

  return (
    <div id="game-hud" className="game-hud">
      {gameState.isPlaying && (
        <>
          {/* Top center - Status bar matching home-status-bar */}
          <PlayerStatusBar
            className="hud-status-bar"
            mode="hud"
            worldId={world.id}
            score={gameState.gameStats.score}
            lives={shopState.lives}
            coins={shopState.coins}
            gems={shopState.gems}
            streak={streakStatus.streak}
            canClaimStreak={streakStatus.canClaim}
          />
        </>
      )}
    </div>
  );
};

// Memoize to prevent unnecessary re-renders
const HUD = memo(HUDComponent);

export default HUD;
